import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import {
  CARD_COLUMN_GAP_PX,
  type CardPosition,
  cardHeightPx,
  columnCount,
  layoutCards,
} from "./cards";
import { rootFontPx } from "./root-font";

export type { CardPosition };

export interface CardLayout {
  /** Ref контейнера, внутри которого раскладываются карточки. */
  containerRef: (element: HTMLElement | null) => void;
  /** Сам контейнер — окно карточек меряет по нему видимую полосу. */
  container: HTMLElement | null;
  /** `null`, пока контейнер не измерен (в том числе статический рендер без DOM). */
  positions: Map<string, CardPosition> | null;
  /** `top` и высоты карточек по порядку `cardKeys` — входы окна карточек; пусты вместе с `positions === null`. */
  tops: readonly number[];
  heights: readonly number[];
  height: number;
}

const EMPTY: readonly number[] = [];

/**
 * Раскладка карточек §9 (1.21) «masonry»: все карточки — прямые дети одного
 * контейнера, позиция каждой — `left`/`top`/`width`, посчитанные `layoutCards`.
 * Абсолютное позиционирование не пересоздаёт карточку при переходе между
 * колонками — в отличие от прежней раскладки по колонкам-`div`, которая рвала
 * фокус на живом потоке (design review, блокер A).
 *
 * Высоты карточек не меряются, а считаются из числа строк (`cardHeightPx`,
 * DEN-104, design stage9-cards-layout-no-measure Д1). Прежнее чтение
 * `offsetHeight` всех карточек на каждую смену состава — а на живом потоке
 * это ~17 раз в секунду — было принудительной раскладкой всего вида. Меряется
 * только контейнер: ширина и корневой шрифт.
 *
 * Отдаёт callback-ref для контейнера, а не `RefObject`: контейнер вида
 * пересоздаётся при переходе «пусто → карточки», и подписка по объекту-ссылке
 * осталась бы на старом, уже снятом элементе.
 *
 * `cardKeys` и `rowCounts` — стабильные ссылки, пока состав, порядок и число
 * строк карточек прежние: раскладка не пересчитывается на каждый срез.
 */
export function useCardLayout(
  cardKeys: readonly string[],
  rowCounts: readonly number[],
): CardLayout {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const containerRef = useCallback((next: HTMLElement | null) => setContainer(next), []);

  const [frame, setFrame] = useState<{ width: number; remPx: number } | null>(null);

  // Ширина контейнера и корневой шрифт меряются в layout-эффекте, а не в
  // обычном: обычный эффект сработал бы уже после кадра, который человек
  // успевает увидеть, — переход «1 → N» колонок был бы виден на глаз.
  // Дальше следит `ResizeObserver` контейнера. Он же ловит смену шрифта по
  // умолчанию на открытой вкладке: отступ обёртки вида (`p-3`) в rem, и ширина
  // контейнера меняется вместе со шрифтом (e2e `cards-layout-height`). Убрали
  // отступ в rem — нужен отдельный сигнал смены шрифта.
  useLayoutEffect(() => {
    if (container === null) {
      return;
    }
    const measure = () => {
      const width = container.getBoundingClientRect().width;
      const remPx = rootFontPx();
      setFrame((previous) =>
        previous !== null && previous.width === width && previous.remPx === remPx
          ? previous
          : { width, remPx },
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [container]);

  const layout = useMemo(() => {
    if (frame === null || frame.width <= 0 || cardKeys.length === 0) {
      return null;
    }
    const heights = rowCounts.map((count) => cardHeightPx(count, frame.remPx));
    const computed = layoutCards(
      heights,
      columnCount(frame.width),
      frame.width,
      CARD_COLUMN_GAP_PX,
    );
    const positions = new Map<string, CardPosition>();
    cardKeys.forEach((key, index) => {
      const position = computed.positions[index];
      if (position !== undefined) {
        positions.set(key, position);
      }
    });
    const tops = computed.positions.map((position) => position.top);
    return { positions, tops, heights, height: computed.height };
  }, [cardKeys, rowCounts, frame]);

  return {
    containerRef,
    container,
    positions: layout?.positions ?? null,
    tops: layout?.tops ?? EMPTY,
    heights: layout?.heights ?? EMPTY,
    height: layout?.height ?? 0,
  };
}
